import React from "react";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import { Checkbox } from "@mui/material";
import ForwardRoundedIcon from "@mui/icons-material/ForwardRounded";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { checkIsCheckoutEnabledListing } from "repoV2/features/Listing/utils/checkIsCheckoutEnabledListing";
import { colorWithAlpha } from "repoV2/features/Common/modules/Colors/utils/rgbColors.utils";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { useCheckoutThemeContext } from "repoV2/features/CheckoutPages/modules/CheckoutThemeContextProvider";
import { appendURLSearchParams } from "repoV2/utils/urls&routing/urlParams";
import { IListing } from "repoV2/features/Listing/Listing.interfaces";
import { constructRoute } from "repoV2/utils/urls&routing/routing";
import { classNames } from "repoV2/utils/common/render/classNames";
import { APP_ROUTE_KEYS } from "repoV2/constants/urls&routing/routing";
import { getCurrencyLocaleValue } from "repoV2/features/Common/modules/Location/utils/getLocales";
import { formatNumberWithCommas } from "repoV2/features/Common/utils/numbers";
import QuantitySelector from "../QuantitySelector/QuantitySelector";
import { IOrderBumpsFormValues } from "../../OrderBumpsFormGroup.interfaces";
import styles from "./OrderBumpsCard.module.scss";

const OrderBumpsCard = (props: {
    orderBumpsListingData: IListing.IUpsellListing;
    handleOrderBumpsSelection: ({
        checked,
        currentListing,
        newListingQuantity,
    }: {
        checked: boolean;
        currentListing: IListing.IUpsellListing;
        newListingQuantity: number;
    }) => void;
    selectedOrderBumps: IOrderBumpsFormValues;
    enableRedirectionOnOrderBumps: boolean;
}) => {
    const {
        orderBumpsListingData,
        selectedOrderBumps = {},
        handleOrderBumpsSelection,
        enableRedirectionOnOrderBumps,
    } = props;

    const { notify, toast_type_keys } = useToastMessage();

    const orderBumpListingUuid = orderBumpsListingData.additional_listing_uid;
    const isMerchandiseSelected =
        orderBumpsListingData.type === LISTING_TYPES.MERCHANDISE &&
        selectedOrderBumps[orderBumpListingUuid];
    const listingQuantity =
        selectedOrderBumps[orderBumpListingUuid]?.selectedQuantity || 1;

    const { hidePriceForZeroPriceListingOrderBumps } =
        useCheckoutBasicDetailsContext();

    const {
        colors: { color1 },
    } = useCheckoutThemeContext();
    const orderBumpBackgroundColor = colorWithAlpha(color1, 0.1);
    const listingSelectionBackgroundColor = colorWithAlpha(color1, 0.35);

    const {
        title = "",
        description = "",
        purchase_button_cta: checkboxText = "",
        currency = "",
        currency_name: currencyName = "",
        price_per_head: listingPrice = 0,
        updated_price: orderBumpListingPrice = 0,
        show_strikethrough: showStrikeOffListingPrice = false,
    } = orderBumpsListingData;

    const hidePrice =
        hidePriceForZeroPriceListingOrderBumps && !orderBumpListingPrice;

    const handleQuantityIncrement = () => {
        const {
            total_inventory: totalInventory = 1,
            purchased_inventory: purchasedInventory = 0,
        } = orderBumpsListingData;
        if (listingQuantity + 1 > totalInventory - purchasedInventory) {
            notify("Maximum available inventory reached!", {
                type: toast_type_keys.error,
            });
            return;
        }
        handleOrderBumpsSelection({
            checked: true,
            currentListing: orderBumpsListingData,
            newListingQuantity: listingQuantity + 1,
        });
    };

    const handleQuantityDecrement = () => {
        handleOrderBumpsSelection({
            checked: listingQuantity - 1 > 0,
            currentListing: orderBumpsListingData,
            newListingQuantity: listingQuantity - 1,
        });
    };

    const handleOrderBumpRedirectionClick = () => {
        if (!enableRedirectionOnOrderBumps) {
            return;
        }
        let redirectionUrl = "";

        if (
            orderBumpsListingData.type &&
            checkIsCheckoutEnabledListing({
                listingType: orderBumpsListingData.type,
            })
        ) {
            redirectionUrl = appendURLSearchParams(
                constructRoute({
                    appRouteKey: APP_ROUTE_KEYS.CHECKOUT_LISTING,
                    args: {
                        slug: orderBumpsListingData.additional_listing_uid,
                    },
                }),
                {}
            );
        } else {
            redirectionUrl = appendURLSearchParams(
                constructRoute({
                    appRouteKey: APP_ROUTE_KEYS.EVENT,
                    args: {
                        slug: orderBumpsListingData.additional_listing_uid,
                    },
                }),
                {}
            );
        }
        window.open(redirectionUrl, "_blank");
    };

    const currencyLocale = getCurrencyLocaleValue(currencyName);

    return (
        <div
            className={styles.orderBumpsCardContainer}
            style={{
                backgroundColor: orderBumpBackgroundColor,
            }}
        >
            <div
                onClick={handleOrderBumpRedirectionClick}
                className={classNames(
                    styles.orderBumpsCardItemContainer,
                    enableRedirectionOnOrderBumps && styles.redirectionStyle
                )}
            >
                <div className={styles.titleStyle}>{title}</div>
                <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: description,
                    }}
                    className={styles.descriptionStyle}
                />
                {!hidePrice && (
                    <div className={styles.priceContainer}>
                        <span
                            className={styles.upsellPriceStyle}
                        >{`${currency}${formatNumberWithCommas({
                            num: orderBumpListingPrice,
                            locale: currencyLocale,
                        })}`}</span>
                        &nbsp;
                        {showStrikeOffListingPrice && (
                            <del
                                className={styles.listingPriceStyle}
                            >{`${currency}${formatNumberWithCommas({
                                num: listingPrice,
                                locale: currencyLocale,
                            })}`}</del>
                        )}
                    </div>
                )}
            </div>
            {isMerchandiseSelected ? (
                <div
                    style={{
                        backgroundColor: listingSelectionBackgroundColor,
                    }}
                    className={styles.selectioncontainerStyle}
                >
                    <QuantitySelector
                        currentQuantity={listingQuantity}
                        quantityName="Item"
                        onIncrease={handleQuantityIncrement}
                        onDecrease={handleQuantityDecrement}
                    />
                </div>
            ) : (
                <div
                    style={{
                        backgroundColor: listingSelectionBackgroundColor,
                    }}
                    className={styles.selectioncontainerStyle}
                >
                    <ForwardRoundedIcon
                        className={classNames(
                            styles.arrowIconStyle,
                            styles.blinkStyle
                        )}
                    />
                    <div className={styles.checkboxContainer}>
                        <Checkbox
                            className={styles.checkboxStyle}
                            classes={{
                                checked: styles.checkedCheckboxStyle,
                            }}
                            size="small"
                            onChange={e =>
                                handleOrderBumpsSelection({
                                    checked: e?.target?.checked,
                                    currentListing: orderBumpsListingData,
                                    newListingQuantity: 1,
                                })
                            }
                            checked={Boolean(
                                selectedOrderBumps[orderBumpListingUuid]
                            )}
                        />
                    </div>

                    <div className={styles.selectionTextStyle}>
                        {checkboxText}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderBumpsCard;
